import { Injectable } from '@angular/core';
import { SharedConfig } from '@takectrl-multiapp/svcs-share-config';

interface CacheItem {
  data: any;
  timestamp: number;
  expiresIn: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiCacheService {
  private readonly STORAGE_KEY = 'api_cache';
  private cache = new Map<string, CacheItem>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.cache = new Map(Object.entries(data));
      }
    } catch (error) {
      console.error('Error loading cache from storage:', error);
      this.cache.clear();
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private saveToStorage(): void {
    try {
      const data = Object.fromEntries(this.cache);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving cache to storage:', error);
    }
  }

  handleUserSessionChange(newUserId?: string): void {
    if (SharedConfig.firebaseUser?.uid) {
      this.clearByPrefix(`user_${SharedConfig.firebaseUser.uid}`);
    }

    if (newUserId) {
      SharedConfig.firebaseUser.uid = newUserId;
    } else {
      SharedConfig.firebaseUser.uid = null;
    }

    this.clearByPrefix('user_anonymous');
    this.saveToStorage();
  }

  set(key: string, data: any, expiresInMinutes: number = 5): void {
    const userPrefix = SharedConfig.firebaseUser?.uid 
      ? `user_${SharedConfig.firebaseUser.uid}` 
      : 'user_anonymous';
    const fullKey = `${userPrefix}_${key}`;
    
    const cacheItem: CacheItem = {
      data,
      timestamp: Date.now(),
      expiresIn: expiresInMinutes * 60 * 1000,
    };

    this.cache.set(fullKey, cacheItem);
    this.saveToStorage();
  }

  get(key: string): any {
    const userPrefix = SharedConfig.firebaseUser?.uid 
      ? `user_${SharedConfig.firebaseUser.uid}` 
      : 'user_anonymous';
    const fullKey = `${userPrefix}_${key}`;
    
    const cached = this.cache.get(fullKey);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > cached.expiresIn;
    if (isExpired) {
      this.cache.delete(fullKey);
      this.saveToStorage();
      return null;
    }
    return cached.data;
  }

  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
    this.saveToStorage();
  }

  clearByPrefix(prefix: string): void {
    let hasChanges = false;
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
        hasChanges = true;
      }
    }
    if (hasChanges) {
      this.saveToStorage();
    }
  }

  getUserKeys(userId: string): string[] {
    return Array.from(this.cache.keys())
      .filter((key) => key.includes(`user_${userId}`));
  }

  // Optional: Method to check storage size
  private checkStorageSize(): boolean {
    try {
      const size = new Blob([localStorage.getItem(this.STORAGE_KEY) || '']).size;
      const maxSize = 5 * 1024 * 1024; // 5MB limit
      return size < maxSize;
    } catch (error) {
      console.error('Error checking storage size:', error);
      return false;
    }
  }

  // Optional: Method to clean up old entries
  private cleanupOldEntries(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > value.expiresIn) {
        this.cache.delete(key);
      }
    }
    this.saveToStorage();
  }
}